import { ReactNode, useMemo, useState } from "react"
import { Contact } from "@/schema"
import { InlineSelect } from "@ui/forms/inline"
import { type LucideIcon } from "lucide-react"

import { useFetchContacts } from "@/hooks/api/contacts"

export interface InlineContactSelectProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedContact?: Contact | null
  setSelectedContactId: (id: string | null) => void
  onSave?: (id: string | null) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
}

export function InlineContactSelect({
  label,
  LabelIcon,
  selectedContact,
  setSelectedContactId,
  onSave,
  isDisabled,
  fixedLabelWidth,
  hideLabel,
  size,
}: InlineContactSelectProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const { data: contactsResponse, isPending } = useFetchContacts(searchQuery)

  const options = useMemo(() => {
    const selectedOption =
      selectedContact && selectedContact.id
        ? {
            value: selectedContact.id,
            label: selectedContact.person?.full_name || "Unnamed contact",
          }
        : null

    const otherOptions =
      contactsResponse?.contacts
        ?.filter((contact) => contact.id !== selectedContact?.id)
        .map((contact) => ({
          value: contact.id,
          label: contact.person?.full_name || "Unnamed contact",
        })) || []

    return selectedOption ? [selectedOption, ...otherOptions] : otherOptions
  }, [contactsResponse?.contacts, selectedContact])

  const handleValueChange = async (value: string | null) => {
    setSelectedContactId(value)
    if (onSave) {
      await onSave(value)
    }
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  return (
    <InlineSelect
      label={label}
      LabelIcon={LabelIcon}
      defaultValue={selectedContact?.id}
      placeholder={isPending ? "Loading..." : "Select contact..."}
      options={options}
      disabled={isPending || isDisabled}
      onSave={handleValueChange}
      onSearch={handleSearch}
      isSearching={isPending}
      fixedLabelWidth={fixedLabelWidth}
      hideLoader
      hideLabel={hideLabel}
      size={size}
    />
  )
}
